import { Users, Droplets, Weight, Plus, Check, ChevronRight, Star, Images } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { Zelt, ZeltTag } from "@/data/zelte";

interface ZeltCardProps {
  zelt: Zelt;
  onNavigate: (path: string) => void;
  onToggleVergleich?: (zelt: Zelt) => void;
  isInVergleich?: boolean;
}

const tagLabels: Record<ZeltTag, string> = {
  neu: "Neu",
  bestseller: "Bestseller",
  sale: "Sale",
  empfohlen: "Empfohlen",
  leicht: "Ultraleicht",
  premium: "Premium",
};

export function ZeltCard({ zelt, onNavigate, onToggleVergleich, isInVergleich = false }: ZeltCardProps) {
  const hasDiscount = zelt.originalPreis && zelt.originalPreis > zelt.preis;
  const discountPercent = hasDiscount
    ? Math.round((1 - zelt.preis / zelt.originalPreis!) * 100)
    : 0;

  return (
    <Card
      className={cn(
        "group relative overflow-hidden border-0 cursor-pointer",
        "bg-card/50 backdrop-blur-sm",
        "shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05),0_4px_16px_-4px_rgba(0,0,0,0.04)]",
        "hover:shadow-[0_12px_32px_-8px_rgba(0,0,0,0.12),0_24px_48px_-12px_rgba(0,0,0,0.08)]",
        "transition-all duration-500 ease-out",
        "hover:-translate-y-2",
        "gradient-border-animated"
      )}
      onClick={() => onNavigate(`/zelte/${zelt.slug}`)}
    >
      {/* Image Container */}
      <div className="relative aspect-[4/3] bg-gradient-to-br from-muted to-muted/50 overflow-hidden">
        {/* Image with shine effect */}
        <div className="absolute inset-0 image-shine">
          <img
            src={zelt.bilder[0]}
            alt={zelt.name}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
            loading="lazy"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = `https://placehold.co/800x600/22c55e/ffffff?text=${encodeURIComponent(zelt.name)}`;
            }}
          />
        </div>

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {/* Tags */}
        {zelt.tags.length > 0 && (
          <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 z-10">
            {zelt.tags.slice(0, 2).map(tag => (
              <Badge
                key={tag}
                variant={tag as any}
                className="text-[10px] px-2.5 py-1 font-semibold shadow-lg backdrop-blur-md"
              >
                {tagLabels[tag]}
              </Badge>
            ))}
          </div>
        )}

        {/* Discount Badge */}
        {hasDiscount && (
          <div className="absolute top-3 right-3 z-10">
            <Badge
              variant="sale"
              className="text-xs font-bold px-2.5 py-1 shadow-lg animate-pulse"
            >
              -{discountPercent}%
            </Badge>
          </div>
        )}

        {/* Compare Button */}
        {onToggleVergleich && (
          <Button
            size="icon"
            variant={isInVergleich ? "default" : "secondary"}
            className={cn(
              "absolute bottom-3 right-3 z-10 h-10 w-10 rounded-xl",
              "shadow-lg backdrop-blur-md border border-white/20",
              "transition-all duration-300",
              isInVergleich
                ? "opacity-100 scale-100 bg-primary hover:bg-primary/90"
                : "opacity-100 translate-y-0 sm:opacity-0 sm:translate-y-2 sm:group-hover:opacity-100 sm:group-hover:translate-y-0 bg-white/90 dark:bg-black/60 hover:bg-white dark:hover:bg-black/80"
            )}
            onClick={e => {
              e.stopPropagation();
              onToggleVergleich(zelt);
            }}
            aria-label={isInVergleich ? `${zelt.name} aus Vergleich entfernen` : `${zelt.name} zum Vergleich hinzufügen`}
          >
            {isInVergleich ? (
              <Check className="h-4 w-4" />
            ) : (
              <Plus className="h-4 w-4 text-foreground" />
            )}
          </Button>
        )}

        {/* Multiple images indicator */}
        {zelt.bilder.length > 1 && (
          <div className="absolute bottom-3 left-3 z-10 flex items-center gap-1.5 bg-black/60 backdrop-blur-md text-white text-xs px-2.5 py-1.5 rounded-lg opacity-90">
            <Images className="h-3.5 w-3.5" />
            <span>{zelt.bilder.length}</span>
          </div>
        )}

        {/* Quick Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out">
          <div className="flex items-center justify-between gap-2 text-white text-xs font-medium">
            <div className="flex items-center gap-3 bg-black/40 backdrop-blur-md rounded-lg px-3 py-1.5">
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {zelt.personen}P
              </span>
              <span className="flex items-center gap-1">
                <Droplets className="h-3 w-3" />
                {zelt.wassersaeule}mm
              </span>
              <span className="flex items-center gap-1">
                <Weight className="h-3 w-3" />
                {zelt.gewicht}kg
              </span>
            </div>
          </div>
        </div>
      </div>

      <CardContent className="p-5">
        {/* Brand & Season */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-primary uppercase tracking-wider">
            {zelt.marke}
          </span>
          <span className="text-xs text-muted-foreground bg-muted/60 px-2 py-0.5 rounded-full">
            {zelt.saison}-Saison
          </span>
        </div>

        {/* Title */}
        <h3 className="font-bold text-lg mb-2 line-clamp-1 group-hover:text-primary transition-colors duration-300">
          {zelt.name}
        </h3>

        {/* Description */}
        <p className="text-sm text-muted-foreground line-clamp-2 mb-4 leading-relaxed">
          {zelt.kurzbeschreibung}
        </p>

        {/* Rating */}
        <div className="flex items-center gap-2 mb-4" role="img" aria-label={`Bewertung: ${zelt.bewertung} von 5 Sternen`}>
          <div className="flex items-center gap-1" aria-hidden="true">
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                className={cn(
                  "w-4 h-4 transition-colors",
                  i < Math.floor(zelt.bewertung)
                    ? "fill-amber-400 text-amber-400"
                    : i < zelt.bewertung
                    ? "fill-amber-400/50 text-amber-400"
                    : "fill-muted text-muted"
                )}
              />
            ))}
          </div>
          <span className="text-sm font-medium">{zelt.bewertung}</span>
          <span className="text-xs text-muted-foreground">
            ({zelt.bewertungAnzahl} Bewertungen)
          </span>
        </div>

        {/* Price & CTA */}
        <div className="flex items-center justify-between pt-4 border-t border-border/50">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold tracking-tight">{zelt.preis}€</span>
            {hasDiscount && (
              <span className="text-sm text-muted-foreground line-through">
                {zelt.originalPreis}€
              </span>
            )}
          </div>
          <Button
            size="sm"
            variant="ghost"
            className={cn(
              "group/btn h-9 px-4 rounded-lg",
              "bg-primary/5 hover:bg-primary hover:text-primary-foreground",
              "transition-all duration-300"
            )}
            onClick={e => {
              e.stopPropagation();
              onNavigate(`/zelte/${zelt.slug}`);
            }}
          >
            <span className="text-sm font-medium">Details</span>
            <ChevronRight className="h-4 w-4 ml-1 transition-transform duration-300 group-hover/btn:translate-x-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
